import { Building2, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';

const plans = [
  {
    name: 'Starter',
    price: 299,
    features: [
      'Up to 200 active positions',
      'Advanced AI-powered matching',
      'GitHub SSO integration',
      'Google SSO integration',
      'Essential recruitment tools',
      'Core analytics dashboard',
      'Basic candidate search',
      'Job posting templates',
      'Email notifications',
      'Standard job board integration'
    ]
  },
  {
    name: 'Business',
    price: 499,
    popular: true,
    description: 'Includes all Starter features, plus:',
    features: [
      'Up to 400 active positions',
      'Standard SSO integration suite',
      'Priority technical support',
      'Advanced branding capabilities',
      'Custom application forms',
      'Bulk candidate import/export',
      'Advanced search filters',
      'Interview scheduling',
      'Team collaboration tools',
      'Custom workflow automation'
    ]
  },
  {
    name: 'Enterprise',
    price: 999,
    description: 'Includes all Business features, plus:',
    features: [
      'Unlimited active positions',
      'Complete SSO suite',
      'Strategic account management',
      'Custom API integrations',
      '24/7 premium support with SLA',
      'Dedicated success manager',
      'Custom analytics dashboards',
      'Advanced compliance tools',
      'Multi-region support',
      'Enterprise-grade security'
    ]
  }
];

export function PlansPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <Building2 className="h-6 w-6" />
            <span className="text-xl font-bold">Job AI</span>
          </Link>
          <div className="space-x-4">
            <Button variant="ghost" asChild>
              <Link to="/login">Sign In</Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Plans Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">Choose Your Plan</h1>
            <p className="text-xl text-muted-foreground">
              Select the perfect plan for your recruitment needs
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan) => (
              <Card key={plan.name} className={`relative flex flex-col ${plan.popular ? 'border-primary shadow-lg' : ''}`}>
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-primary text-primary-foreground text-sm font-medium px-3 py-1 rounded-full">
                      RECOMMENDED
                    </span>
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="text-2xl text-center">{plan.name}</CardTitle>
                  <div className="text-center mt-4">
                    <span className="text-4xl font-bold">${plan.price}</span>
                    <span className="text-muted-foreground">/month</span>
                  </div>
                </CardHeader>
                <CardContent className="flex-grow">
                  {plan.description && (
                    <p className="text-sm text-muted-foreground mb-3 italic">
                      {plan.description}
                    </p>
                  )}
                  <ul className="space-y-3">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start">
                        <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter className="mt-auto pt-6">
                  <Button 
                    className="w-full" 
                    variant={plan.popular ? 'default' : 'outline'}
                    asChild
                  >
                    <Link to="/login">
                      Pay Now
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}